import { useState, useEffect, lazy, Suspense, memo, Fragment } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useParams } from 'react-router-dom';
import { ContentProvider, useContent } from './context/ContentContext';
import { useLanguage } from './hooks/useLanguage';
import { ErrorBoundary, SkipLink, ToastProvider } from './components/ui';
import { getRouteMeta } from './lib/route-meta';
import type { Language, LanguageTranslations } from './types';


// Eagerly loaded components (above the fold + SSR-rendered pages)
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Footer from './components/Footer';
import Analytics from './components/Analytics';
import Page from './components/Page';
import CaseStudies from './components/CaseStudies';
import Articles from './components/Articles';
import Resources from './components/Resources';
import TechPartner from './components/TechPartner';

// Lazily loaded components (below the fold)
const LogoMarquee = lazy(() => import('./components/LogoMarquee'));
const ProblemsWeSolve = lazy(() => import('./components/ProblemsWeSolve'));
const Services = lazy(() => import('./components/Services'));
const TestimonialBar = lazy(() => import('./components/TestimonialBar'));
const Process = lazy(() => import('./components/Process'));
const Testimonials = lazy(() => import('./components/Testimonials'));
const WhyUs = lazy(() => import('./components/WhyUs'));
const Contact = lazy(() => import('./components/Contact'));
const ServiceDetail = lazy(() => import('./components/ServiceDetail'));
const About = lazy(() => import('./components/About'));
const Admin = lazy(() => import('./components/Admin'));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const ContentDraftsList = lazy(() => import('./components/admin/ContentDraftsList'));
const ContentDraftReview = lazy(() => import('./components/admin/ContentDraftReview'));
const ContentTopicsList = lazy(() => import('./components/admin/ContentTopicsList'));
const RssFeedsList = lazy(() => import('./components/admin/RssFeedsList'));
const LogsViewer = lazy(() => import('./components/admin/LogsViewer'));
const AdminSettings = lazy(() => import('./components/admin/AdminSettings'));
const PromptsManager = lazy(() => import('./components/admin/PromptsManager'));
const LinkedInSchedule = lazy(() => import('./components/admin/LinkedInSchedule'));
const SeoIntelligence = lazy(() => import('./components/admin/SeoIntelligence'));
const FloatingButtons = lazy(() => import('./components/FloatingButtons'));
const AccessibilityWidget = lazy(() => import('./components/AccessibilityWidget'));
const ContactPage = lazy(() => import('./components/ContactPage'));
const NotFound = lazy(() => import('./components/NotFound'));

// Loading fallback component
const SectionLoader = memo(function SectionLoader() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-4 border-[#4f6cff] border-t-transparent rounded-full animate-spin" />
    </div>
  );
});

// Home page component
interface HomePageProps {
  t: LanguageTranslations;
  lang: Language;
}

const HomePage = memo<HomePageProps>(function HomePage({ t, lang }) {
  return (
    <>
      <Hero t={t.hero} lang={lang} />
      <Suspense fallback={<SectionLoader />}>
        <LogoMarquee />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <ProblemsWeSolve t={t.problems} lang={lang} />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <section id="services" className="scroll-mt-20">
          <Services t={t.services} lang={lang} />
        </section>
      </Suspense>
      <Suspense fallback={null}>
        <TestimonialBar lang={lang} />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <WhyUs t={t.whyUs} lang={lang} />
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <section id="process" className="scroll-mt-20">
          <Process t={t.process} lang={lang} />
        </section>
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <section id="testimonials" className="scroll-mt-20">
          <Testimonials lang={lang} />
        </section>
      </Suspense>
      <Suspense fallback={<SectionLoader />}>
        <section id="contact" className="scroll-mt-20">
          <Contact t={t.contact} />
        </section>
      </Suspense>
    </>
  );
});

// Layout component that wraps all pages
interface LayoutProps {
  children: React.ReactNode;
}

const Layout = memo<LayoutProps>(function Layout({ children }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { lang, localizedPath, toggleLanguage, isRTL } = useLanguage();
  const { content } = useContent();

  const t = content[lang];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top on route change, or to hash if present
  useEffect(() => {
    if (location.hash) {
      const targetId = location.hash.slice(1);
      const scrollToElement = (attempts = 0) => {
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else if (attempts < 20) {
          setTimeout(() => scrollToElement(attempts + 1), 100);
        }
      };
      scrollToElement();
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [lang, isRTL]);

  useEffect(() => {
    const meta = getRouteMeta(location.pathname);
    document.title = meta.title;
  }, [location.pathname]);

  return (
    <div className={`relative min-h-screen ${lang === 'he' ? 'font-heebo' : 'font-inter'}`}>
      <Analytics />
      <SkipLink />
      <header role="banner">
        <Navbar
          isScrolled={isScrolled}
          lang={lang}
          toggleLang={toggleLanguage}
          localizedPath={localizedPath}
        />
      </header>

      <main id="main-content">{children}</main>

      <Footer t={t.footer} localizedPath={localizedPath} />
      <Suspense fallback={null}>
        <FloatingButtons lang={lang} />
      </Suspense>
      <Suspense fallback={null}>
        <AccessibilityWidget />
      </Suspense>
    </div>
  );
});

// Page wrapper components using context
const HomePageWrapper = memo(function HomePageWrapper() {
  const { lang } = useLanguage();
  const { content } = useContent();
  const t = content[lang];

  return (
    <Layout>
      <HomePage t={t} lang={lang} />
    </Layout>
  );
});

const AboutPageWrapper = memo(function AboutPageWrapper() {
  return (
    <Layout>
      <Suspense fallback={<SectionLoader />}>
        <About />
      </Suspense>
    </Layout>
  );
});

interface ServicePageWrapperProps {
  serviceId: string;
}

const ServicePageWrapper = memo<ServicePageWrapperProps>(function ServicePageWrapper({
  serviceId,
}) {
  const { lang } = useLanguage();

  return (
    <Layout>
      <Suspense fallback={<SectionLoader />}>
        <ServiceDetail serviceId={serviceId} lang={lang} />
      </Suspense>
    </Layout>
  );
});

// Page wrapper for CMS pages (Privacy, Terms, etc.)
interface PageWrapperProps {
  slug: string;
}

const PageWrapper = memo<PageWrapperProps>(function PageWrapper({ slug }) {
  return (
    <Layout>
      <Page slug={slug} />
    </Layout>
  );
});

// Case Studies list page wrapper
const CaseStudiesPageWrapper = memo(function CaseStudiesPageWrapper() {
  return (
    <Layout>
      <CaseStudies />
    </Layout>
  );
});

// Contact page wrapper
const ContactPageWrapper = memo(function ContactPageWrapper() {
  return (
    <Layout>
      <Suspense fallback={<SectionLoader />}>
        <ContactPage />
      </Suspense>
    </Layout>
  );
});

// Articles list page wrapper
const ArticlesPageWrapper = memo(function ArticlesPageWrapper() {
  return (
    <Layout>
      <Articles />
    </Layout>
  );
});

// Resources list page wrapper
const ResourcesPageWrapper = memo(function ResourcesPageWrapper() {
  return (
    <Layout>
      <Resources />
    </Layout>
  );
});

// Resource detail page wrapper (uses Page component with dynamic slug from URL)
const ResourceDetailWrapper = memo(function ResourceDetailWrapper() {
  const { slug } = useParams<{ slug: string }>();

  if (!slug) {
    return <NotFoundPageWrapper />;
  }

  return (
    <Layout>
      <Page slug={slug} />
    </Layout>
  );
});

// Article detail page wrapper (uses Page component with dynamic slug from URL)
const ArticleDetailWrapper = memo(function ArticleDetailWrapper() {
  const { slug } = useParams<{ slug: string }>();

  if (!slug) {
    return <NotFoundPageWrapper />;
  }

  return (
    <Layout>
      <Page slug={slug} />
    </Layout>
  );
});

// Case Study detail page wrapper (uses Page component with dynamic slug from URL)
const CaseStudyDetailWrapper = memo(function CaseStudyDetailWrapper() {
  const { slug } = useParams<{ slug: string }>();
  
  if (!slug) {
    return <NotFoundPageWrapper />;
  }
  
  return (
    <Layout>
      <Page slug={slug} />
    </Layout>
  );
});

// 404 Not Found page wrapper
const NotFoundPageWrapper = memo(function NotFoundPageWrapper() {
  return (
    <Suspense fallback={<SectionLoader />}>
      <NotFound />
    </Suspense>
  );
});

// Admin page (no language prefix, no layout)
const AdminPage = memo(function AdminPage() {
  const { content, setContent } = useContent();

  // Admin is always in English (LTR)
  useEffect(() => {
    document.documentElement.dir = 'ltr';
    document.documentElement.lang = 'en';
  }, []);

  return (
    <Suspense fallback={<SectionLoader />}>
      <Admin
        content={content}
        onUpdateContent={setContent}
      />
    </Suspense>
  );
});

// Content engine admin page wrappers (no layout, no language prefix)
const ContentDraftsListPage = memo(function ContentDraftsListPage() {
  return (
    <Suspense fallback={<SectionLoader />}>
      <ContentDraftsList />
    </Suspense>
  );
});

const ContentDraftReviewPage = memo(function ContentDraftReviewPage() {
  return (
    <Suspense fallback={<SectionLoader />}>
      <ContentDraftReview />
    </Suspense>
  );
});

const ContentTopicsListPage = memo(function ContentTopicsListPage() {
  return (
    <Suspense fallback={<SectionLoader />}>
      <ContentTopicsList />
    </Suspense>
  );
});

const RssFeedsListPage = memo(function RssFeedsListPage() {
  return (
    <Suspense fallback={<SectionLoader />}>
      <RssFeedsList />
    </Suspense>
  );
});

const LogsViewerPage = memo(function LogsViewerPage() {
  return (
    <Suspense fallback={<SectionLoader />}>
      <LogsViewer />
    </Suspense>
  );
});

const AdminSettingsPage = memo(function AdminSettingsPage() {
  return (
    <Suspense fallback={<SectionLoader />}>
      <AdminSettings />
    </Suspense>
  );
});

const PromptsManagerPage = memo(function PromptsManagerPage() {
  return (
    <Suspense fallback={<SectionLoader />}>
      <PromptsManager />
    </Suspense>
  );
});

const LinkedInSchedulePage = memo(function LinkedInSchedulePage() {
  return (
    <Suspense fallback={<SectionLoader />}>
      <LinkedInSchedule />
    </Suspense>
  );
});

const SeoIntelligencePage = memo(function SeoIntelligencePage() {
  return (
    <Suspense fallback={<SectionLoader />}>
      <SeoIntelligence />
    </Suspense>
  );
});

// Main App component
function AppRoutes() {
  const { siteLanguages } = useContent();

  // Derive language prefixes from DB; '' = default (EN), others get /{code} prefix
  const nonDefaultCodes = siteLanguages
    .filter((l) => !l.isDefault && l.isActive)
    .map((l) => l.code);
  const LANGUAGE_PREFIXES = ['', ...nonDefaultCodes];

  return (
    <Routes>
      {/* Admin routes (no language prefix) - share AdminLayout (sidebar + auth shell) */}
      <Route
        path="/admin"
        element={
          <Suspense fallback={<SectionLoader />}>
            <AdminLayout />
          </Suspense>
        }
      >
        <Route index element={<AdminPage />} />
        <Route path=":tab" element={<AdminPage />} />
        <Route path="content-drafts" element={<ContentDraftsListPage />} />
        <Route path="content-drafts/:id" element={<ContentDraftReviewPage />} />
        <Route path="content-topics" element={<ContentTopicsListPage />} />
        <Route path="rss-feeds" element={<RssFeedsListPage />} />
        <Route path="logs" element={<LogsViewerPage />} />
        <Route path="settings" element={<AdminSettingsPage />} />
        <Route path="prompts" element={<PromptsManagerPage />} />
        <Route path="linkedin-schedule" element={<LinkedInSchedulePage />} />
        <Route path="seo-intelligence" element={<SeoIntelligencePage />} />
      </Route>

      {/* Standalone Hebrew-only landing page (no shared Layout, no English twin) */}
      <Route path="/he/tech-partner" element={<TechPartner />} />

      {/* Generate routes for all languages dynamically */}
      {LANGUAGE_PREFIXES.map((lang) => {
        const prefix = lang ? `/${lang}` : '';
        return (
          <Fragment key={lang || 'default'}>
            <Route path={prefix || '/'} element={<HomePageWrapper />} />
            <Route path={`${prefix}/about`} element={<AboutPageWrapper />} />
            <Route
              path={`${prefix}/ai-agents`}
              element={<ServicePageWrapper serviceId="ai-agents" />}
            />
            <Route
              path={`${prefix}/integration-services`}
              element={<ServicePageWrapper serviceId="integration" />}
            />
            <Route
              path={`${prefix}/custom-development`}
              element={<ServicePageWrapper serviceId="custom-software" />}
            />
            <Route path={`${prefix}/contact`} element={<ContactPageWrapper />} />
            <Route path={`${prefix}/privacy`} element={<PageWrapper slug="privacy" />} />
            <Route path={`${prefix}/terms`} element={<PageWrapper slug="terms" />} />
            <Route path={`${prefix}/accessibility`} element={<PageWrapper slug="accessibility" />} />
            <Route path={`${prefix}/case-studies`} element={<CaseStudiesPageWrapper />} />
            <Route path={`${prefix}/case-studies/:slug`} element={<CaseStudyDetailWrapper />} />
            <Route path={`${prefix}/articles`} element={<ArticlesPageWrapper />} />
            <Route path={`${prefix}/articles/:slug`} element={<ArticleDetailWrapper />} />
            <Route path={`${prefix}/resources`} element={<ResourcesPageWrapper />} />
            <Route path={`${prefix}/resources/:slug`} element={<ResourceDetailWrapper />} />
          </Fragment>
        );
      })}

      {/* Fallback - 404 page for unknown routes */}
      <Route path="*" element={<NotFoundPageWrapper />} />
    </Routes>
  );
}

/**
 * Inner app shell — everything except the router.
 *
 * entry-client.tsx wraps this with BrowserRouter (client hydration).
 * entry-server.tsx wraps this with StaticRouter (server rendering).
 */
export function AppInner() {
  return (
    <ErrorBoundary>
      <ContentProvider>
        <ToastProvider>
          <AppRoutes />
        </ToastProvider>
      </ContentProvider>
    </ErrorBoundary>
  );
}

/**
 * Default export keeps BrowserRouter for the Vite dev server
 * (which uses entry-client.tsx -> createRoot fallback path).
 */
export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}
